import { getStartEndTimeByRangeType } from '@/utils/history';
import { showProperDateString } from '@/utils/timeUtils';
import { getTokenInfo } from '@/utils/token';
import { getPairId } from '@/utils/transform/transformId';

import { SYN_API_URLS } from '@/constants/api';
import { axiosGet } from '@/utils/axios';

import _ from 'lodash';
import { DEFAULT_HISTORY_PAGE_SIZE } from './constants';
import {
  GetAccountBalanceHistoryParams,
  GetAccountBalanceHistoryResponse,
  GetAllPairBasicInfoByGraphParams,
  GetAllPairBasicInfoByGraphResponse,
  GetFundingHistoryParams,
  GetFundingHistoryResponse,
  GetLiquidityHistoryParams,
  GetLiquidityHistoryResponse,
  GetOrdersHistoryParams,
  GetOrdersHistoryResponse,
  GetTradeHistoryParams,
  GetTradeHistoryResponse,
  GetTransferHistoryParams,
  GetTransferHistoryResponse,
  IGraphInstrumentBasicInfo,
  IGraphPairBasicInfo,
  TradeHistory,
  TransferHistory,
  VirtualTradeType,
} from './types';

const DEFAULT_PAGE = 1;

export const getAllPairBasicInfoByGraph = async (
  params: GetAllPairBasicInfoByGraphParams,
): Promise<GetAllPairBasicInfoByGraphResponse | undefined> => {
  const { chainId, sdk } = params;
  if (chainId && sdk) {
    const infos = await sdk.perpDataSource.statistics.getInstrumentsInfo();
    const basicInstruments: IGraphInstrumentBasicInfo[] = infos.map((info) => ({
      ...info,
      id: info.address,
      instrumentAddress: info.address,
      baseToken: getTokenInfo(info.base, chainId),
      quoteToken: getTokenInfo(info.quote, chainId),
      chainId,
    }));

    const basicPairs = basicInstruments.reduce((acc, info) => {
      const { expiries } = info;
      expiries?.forEach((expiry) => {
        if (expiry) {
          const pairId = getPairId(info.instrumentAddress, expiry);
          if (pairId) {
            acc[pairId] = {
              id: pairId,
              expiry,
              isInverse: info.isInverse,
              rootInstrument: info,
              chainId,
              symbol: `${info.symbol}-${showProperDateString({
                expiry,
                format: 'MMDD',
                showShortPerp: true,
              })}`,
            };
          }
        }
      });

      return acc;
    }, {} as { [pairId: string]: IGraphPairBasicInfo });

    return basicPairs;
  }
};

export const getTradeHistory = async (params: GetTradeHistoryParams): Promise<GetTradeHistoryResponse> => {
  const {
    chainId,
    userAddress,
    instrumentAddress,
    expiry,
    page = 1,
    size: pageSize = DEFAULT_HISTORY_PAGE_SIZE,
    timeRange,
    download = false,
  } = params;

  if (!userAddress) {
    return [];
  }
  const { startTime, endTime } = getStartEndTimeByRangeType(timeRange);
  const result = await axiosGet({
    url: SYN_API_URLS.HISTORY.TRADE,
    config: {
      params: {
        chainId,
        userAddress,
        ...(download
          ? { download: true }
          : {
              ...(pageSize !== undefined && { size: pageSize }),
              ...(instrumentAddress !== undefined && { instrumentAddress }),
              ...(expiry !== undefined && { expiry }),
              ...(startTime !== undefined && { startTime }),
              ...(endTime !== undefined && { endTime }),
              ...(page !== undefined && { page }),
            }),
      },
    },
  });
  const tradeHistoryList = result.data.data.list.map((item: TradeHistory & { typeString: string }) => {
    const typeMapping = item.typeString as VirtualTradeType;

    if (typeMapping === VirtualTradeType.LIQUIDATION) {
      item.side = item.side === 'long' ? 'short' : 'long';
    }

    return {
      ...item,
      typeString: typeMapping,
    };
  });
  console.record('graph', `Query [getTradeHistory]`, tradeHistoryList, {
    chainId,
    userAddress,
  });
  return _.orderBy(tradeHistoryList, ['timestamp'], ['desc']);
};

export const getOrdersHistory = async (params: GetOrdersHistoryParams): Promise<GetOrdersHistoryResponse> => {
  const {
    chainId,
    userAddress,
    instrumentAddress,
    expiry,
    timeRange,
    page = DEFAULT_PAGE,
    size: pageSize = DEFAULT_HISTORY_PAGE_SIZE,
    download = false,
  } = params;

  if (!userAddress) {
    return [];
  }
  const { startTime, endTime } = getStartEndTimeByRangeType(timeRange);
  const result = await axiosGet({
    url: SYN_API_URLS.HISTORY.ORDER,
    config: {
      params: {
        chainId,
        userAddress,
        ...(download
          ? { download: true }
          : {
              ...(pageSize !== undefined && { size: pageSize }),
              ...(instrumentAddress !== undefined && { instrumentAddress }),
              ...(expiry !== undefined && { expiry }),
              ...(startTime !== undefined && { startTime }),
              ...(endTime !== undefined && { endTime }),
              ...(page !== undefined && { page }),
            }),
      },
    },
  });
  const orderHistoryList = result.data.data.list || [];
  console.record('graph', `Query [getOrderHistory]`, orderHistoryList, {
    chainId,
    userAddress,
  });
  return _.orderBy(orderHistoryList, ['timestamp'], ['desc']);
};

export const getFundingHistory = async (params: GetFundingHistoryParams): Promise<GetFundingHistoryResponse> => {
  const {
    chainId,
    userAddress,
    instrumentAddress,
    expiry,
    timeRange,
    page = DEFAULT_PAGE,
    size: pageSize = DEFAULT_HISTORY_PAGE_SIZE,
    download = false,
  } = params;

  if (!userAddress) return [];

  const { startTime, endTime } = getStartEndTimeByRangeType(timeRange);
  const result = await axiosGet({
    url: SYN_API_URLS.HISTORY.FUNDING,
    config: {
      params: {
        chainId,
        userAddress,
        ...(download
          ? { download: true }
          : {
              ...(pageSize !== undefined && { size: pageSize }),
              ...(instrumentAddress !== undefined && { instrumentAddress }),
              ...(expiry !== undefined && { expiry }),
              ...(startTime !== undefined && { startTime }),
              ...(endTime !== undefined && { endTime }),
              ...(page !== undefined && { page }),
            }),
      },
    },
  });

  const historyList = result.data.data.list || [];
  console.record('graph', `Query [getFundingHistory]`, historyList, { chainId, userAddress });

  return historyList;
};

export const getTransferHistory = async (params: GetTransferHistoryParams): Promise<GetTransferHistoryResponse> => {
  const {
    chainId,
    userAddress,
    instrumentAddress,
    expiry,
    timeRange,
    page = DEFAULT_PAGE,
    size: pageSize = DEFAULT_HISTORY_PAGE_SIZE,
    download = false,
  } = params;

  if (!userAddress) return [];

  const { startTime, endTime } = getStartEndTimeByRangeType(timeRange);
  const result = await axiosGet({
    url: SYN_API_URLS.HISTORY.TRANSFER,
    config: {
      params: {
        chainId,
        userAddress,
        ...(download
          ? { download: true }
          : {
              ...(pageSize !== undefined && { size: pageSize }),
              ...(instrumentAddress !== undefined && { instrumentAddress }),
              ...(expiry !== undefined && { expiry }),
              ...(startTime !== undefined && { startTime }),
              ...(endTime !== undefined && { endTime }),
              ...(page !== undefined && { page }),
            }),
      },
    },
  });

  const historyList = result.data.data.list || [];
  console.record('graph', `Query [getTransferHistory]`, historyList, { chainId, userAddress });

  return (
    historyList?.map((history: TransferHistory) => ({
      ...history,
    })) || []
  );
};

export const getLiquidityHistory = async (params: GetLiquidityHistoryParams): Promise<GetLiquidityHistoryResponse> => {
  const {
    chainId,
    userAddress,
    instrumentAddress,
    expiry,
    timeRange,
    page = DEFAULT_PAGE,
    size: pageSize = DEFAULT_HISTORY_PAGE_SIZE,
    download = false,
  } = params;

  if (!userAddress) return [];

  const { startTime, endTime } = getStartEndTimeByRangeType(timeRange);
  const result = await axiosGet({
    url: SYN_API_URLS.HISTORY.LQUIDITY,
    config: {
      params: {
        chainId,
        userAddress,
        ...(download
          ? { download: true }
          : {
              ...(pageSize !== undefined && { size: pageSize }),
              ...(instrumentAddress !== undefined && { instrumentAddress }),
              ...(expiry !== undefined && { expiry }),
              ...(startTime !== undefined && { startTime }),
              ...(endTime !== undefined && { endTime }),
              ...(page !== undefined && { page }),
            }),
      },
    },
  });

  const eventList = result.data.data.list || [];
  console.record('graph', `Query [getLiquidityHistory]`, eventList, { chainId, userAddress });

  return eventList;
};

export const getAccountBalanceHistory = async (
  params: GetAccountBalanceHistoryParams,
): Promise<GetAccountBalanceHistoryResponse> => {
  const {
    chainId,
    userAddress,
    timeRange,
    page = DEFAULT_PAGE,
    size: pageSize = DEFAULT_HISTORY_PAGE_SIZE,
    download = false,
  } = params;

  if (!userAddress) return [];

  const { startTime, endTime } = getStartEndTimeByRangeType(timeRange);
  const result = await axiosGet({
    url: SYN_API_URLS.HISTORY.ACCOUNT,
    config: {
      params: {
        chainId,
        userAddress,

        ...(download
          ? { download: true }
          : {
              ...(startTime !== undefined && { startTime }),
              ...(endTime !== undefined && { endTime }),
              ...(page !== undefined && { page }),
              ...(pageSize !== undefined && { size: pageSize }),
            }),
      },
    },
  });

  const eventList = result.data.data.list || [];
  console.record('graph', `Query [getAccountBalanceHistory]`, eventList, { chainId, userAddress });

  return eventList || [];
};
